import React from "react";
import { getAvatarColor, getInitials } from "../../../utils/avatarUtils";
import { isPendingStudyPlaceholder } from "./isfPlaceholderService";
import "./MilestoneDashboard.scss";

function formatDueDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function MetricCard({ label, value, sub, accent = "default" }) {
  return (
    <div className={`isf-ms-dashboard__metric isf-ms-dashboard__metric--${accent}`}>
      <div className="isf-ms-dashboard__metric-value">{value}</div>
      <div className="isf-ms-dashboard__metric-label">{label}</div>
      {sub ? <div className="isf-ms-dashboard__metric-sub">{sub}</div> : null}
    </div>
  );
}

function PlaceholderMatrixCard({ item }) {
  const ph = item.placeholder || {};
  const completeness = item.completeness || {};
  const sign = item.sign || {};
  const name = ph.PlaceholderName || ph.placeholderName || "Expected document";
  const isPending = isPendingStudyPlaceholder(ph);
  const status = completeness.percent >= 100
    ? "Complete"
    : completeness.uploadComplete
      ? "Signature pending"
      : isPending
        ? "Upload pending"
        : "In progress";
  const statusClass =
    status === "Complete"
      ? "complete"
      : status === "Upload pending"
        ? "pending"
        : "progress";

  return (
    <div className="isf-ms-dashboard__ph-card">
      <div className="isf-ms-dashboard__ph-card-head">
        <span className="isf-ms-dashboard__ph-card-name" title={name}>
          {name}
        </span>
        <span className={`isf-ms-dashboard__ph-card-status isf-ms-dashboard__ph-card-status--${statusClass}`}>
          {status}
        </span>
      </div>
      <div className="isf-ms-dashboard__ph-card-progress">
        <div className="isf-ms-dashboard__ph-card-track">
          <div
            className={`isf-ms-dashboard__ph-card-fill${
              completeness.percent >= 100 ? " is-complete" : ""
            }`}
            style={{ width: `${completeness.percent || 0}%` }}
          />
        </div>
        <strong>{completeness.percent || 0}%</strong>
      </div>
      <div className="isf-ms-dashboard__ph-card-meta">
        <span>
          <i className="fas fa-upload me-1" aria-hidden="true" />
          Upload {completeness.uploadComplete ? "done" : "pending"}
        </span>
        {completeness.requiresSignature ? (
          <span>
            <i className="fas fa-file-signature me-1" aria-hidden="true" />
            Sign {sign.signedCount || 0}/{sign.totalSigners || 0}
          </span>
        ) : (
          <span className="text-muted">No signature</span>
        )}
      </div>
      {ph.DueDate ? (
        <div className="isf-ms-dashboard__ph-card-due">
          Due {formatDueDate(ph.DueDate)}
        </div>
      ) : null}
    </div>
  );
}

export default function MilestoneDashboard({
  milestoneLabel = "",
  milestoneCode = "",
  milestoneName = "",
  dueRule = "",
  dueDate = "",
  actualMilestoneDate = "",
  milestoneStatus = "",
  folderName = "",
  siteName = "",
  completeness = {},
  signatureDone = [],
  signaturePending = [],
}) {
  const details = completeness.details || [];
  const percent = completeness.percent || 0;

  return (
    <div className="isf-ms-dashboard h-100 overflow-auto">
      <div className="isf-ms-dashboard__hero" data-tour="isf-milestone-metrics">
        <div className="isf-ms-dashboard__hero-icon">
          <i className="fas fa-flag" aria-hidden="true" />
        </div>
        <div className="isf-ms-dashboard__hero-body">
          <h5 className="isf-ms-dashboard__title">{milestoneLabel || "Milestone"}</h5>
          <div className="isf-ms-dashboard__chips">
            {siteName ? (
              <span className="isf-ms-dashboard__chip isf-ms-dashboard__chip--site">
                <i className="fas fa-map-marker-alt me-1" aria-hidden="true" />
                {siteName}
              </span>
            ) : null}
            {milestoneCode ? (
              <span className="isf-ms-dashboard__chip">{milestoneCode}</span>
            ) : null}
            {folderName ? (
              <span className="isf-ms-dashboard__chip isf-ms-dashboard__chip--muted">
                <i className="fas fa-folder-open me-1" aria-hidden="true" />
                {folderName}
              </span>
            ) : null}
            {dueRule ? (
              <span className="isf-ms-dashboard__chip isf-ms-dashboard__chip--muted">
                {dueRule}
              </span>
            ) : null}
            {actualMilestoneDate ? (
              <span className="isf-ms-dashboard__chip isf-ms-dashboard__chip--date">
                Actual {formatDueDate(actualMilestoneDate)}
              </span>
            ) : null}
            {milestoneStatus && milestoneStatus !== "Pending" ? (
              <span className="isf-ms-dashboard__chip isf-ms-dashboard__chip--status">
                {milestoneStatus === "NotApplicable" ? "N/A" : milestoneStatus}
              </span>
            ) : null}
            {dueDate ? (
              <span className="isf-ms-dashboard__chip isf-ms-dashboard__chip--due">
                Due {formatDueDate(dueDate)}
              </span>
            ) : null}
          </div>
        </div>
      </div>

      <div className="isf-ms-dashboard__metrics">
        <MetricCard
          label="Completeness"
          value={`${percent}%`}
          sub={percent >= 100 ? "Milestone complete" : "Overall progress"}
          accent={percent >= 100 ? "success" : "primary"}
        />
        <MetricCard
          label="Uploads"
          value={`${completeness.uploadDoneCount || 0}/${completeness.uploadTotalCount || 0}`}
          sub="Documents uploaded"
          accent="upload"
        />
        <MetricCard
          label="Signatures"
          value={
            completeness.requiresSignature
              ? `${completeness.signedCount || 0}/${completeness.totalSigners || 0}`
              : "N/A"
          }
          sub={
            completeness.requiresSignature ? "Signer completions" : "Not required"
          }
          accent="sign"
        />
        <MetricCard
          label="Documents"
          value={`${completeness.uploadDoneCount || 0}/${completeness.placeholderCount || 0}`}
          sub="Uploaded"
          accent="docs"
        />
      </div>

      <div className="isf-ms-dashboard__progress-block">
        <div className="isf-ms-dashboard__progress-head">
          <span>Milestone progress</span>
          <strong>{percent}%</strong>
        </div>
        <div className="isf-ms-dashboard__progress-track">
          <div
            className={`isf-ms-dashboard__progress-fill${
              percent >= 100 ? " is-complete" : ""
            }`}
            style={{ width: `${percent}%` }}
          />
        </div>
        <div className="isf-ms-dashboard__progress-meta">
          Upload {completeness.uploadDoneCount || 0}/{completeness.uploadTotalCount || 0}
          {completeness.requiresSignature
            ? ` · Signature ${completeness.signedCount || 0}/${completeness.totalSigners || 0}`
            : ""}
        </div>
      </div>

      {(signatureDone.length > 0 || signaturePending.length > 0) && (
        <div className="isf-ms-dashboard__signatures">
          <div className="isf-ms-dashboard__section-title">Signature overview</div>
          <div className="isf-ms-dashboard__signature-row">
            <div className="isf-ms-dashboard__signature-col">
              <span className="isf-ms-dashboard__signature-label">
                <i className="fas fa-file-signature" aria-hidden="true" /> Done
              </span>
              <div className="isf-ms-dashboard__signature-avatars">
                {signatureDone.length ? (
                  signatureDone.map((user) => (
                    <span
                      key={user.staffId || user.displayName}
                      className="isf-ms-dashboard__avatar"
                      style={{
                        backgroundColor: getAvatarColor(
                          user.displayName || user.label,
                        ),
                      }}
                      title={user.displayName || user.label}
                    >
                      {getInitials(user.displayName || user.label)}
                    </span>
                  ))
                ) : (
                  <span className="isf-ms-dashboard__empty">None</span>
                )}
              </div>
            </div>
            <div className="isf-ms-dashboard__signature-col">
              <span className="isf-ms-dashboard__signature-label">
                <i className="fas fa-file-signature" aria-hidden="true" /> Pending
              </span>
              <div className="isf-ms-dashboard__signature-avatars">
                {signaturePending.length ? (
                  signaturePending.map((user) => (
                    <span
                      key={user.staffId || user.displayName}
                      className="isf-ms-dashboard__avatar"
                      style={{
                        backgroundColor: getAvatarColor(
                          user.displayName || user.label,
                        ),
                      }}
                      title={user.displayName || user.label}
                    >
                      {getInitials(user.displayName || user.label)}
                    </span>
                  ))
                ) : (
                  <span className="isf-ms-dashboard__empty">None</span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="isf-ms-dashboard__matrix" data-tour="isf-milestone-matrix">
        <div className="isf-ms-dashboard__section-title">
          Expected documents
          <span className="isf-ms-dashboard__section-count">
            {completeness.uploadDoneCount || 0}/{details.length} uploaded
          </span>
        </div>
        {details.length ? (
          <div className="isf-ms-dashboard__matrix-grid">
            {details.map((item) => (
              <PlaceholderMatrixCard
                key={
                  item.placeholder?.StudyPlaceholderId ||
                  item.placeholder?.PlaceholderName
                }
                item={item}
              />
            ))}
          </div>
        ) : (
          <div className="isf-ms-dashboard__matrix-empty">
            No expected documents linked to this milestone yet.
          </div>
        )}
      </div>
    </div>
  );
}
